import React, { useState, useEffect } from 'react';
import { Youtube, Users, X, Search, Globe, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const YouTubeChannels = () => {
  const [topChannels, setTopChannels] = useState([]);
  const [allChannels, setAllChannels] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0 });
  const [totalChannels, setTotalChannels] = useState(0);

  useEffect(() => {
    fetchTopChannels();
  }, []);

  const fetchTopChannels = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/youtube/channels/top?limit=10`);
      setTopChannels(response.data.channels || []);
      
      const allResponse = await axios.get(`${API_URL}/api/youtube/channels?limit=1`);
      setTotalChannels(allResponse.data.total || 500);
    } catch (error) {
      console.error('Error fetching top channels:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllChannels = async (skip = 0, search = '') => {
    try {
      setModalLoading(true);
      const params = new URLSearchParams({
        limit: '20',
        skip: skip.toString()
      });
      
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API_URL}/api/youtube/channels?${params}`);
      setAllChannels(response.data.channels || []);
      setPagination({
        skip: response.data.skip,
        limit: response.data.limit,
        total: response.data.total
      });
    } catch (error) {
      console.error('Error fetching all channels:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const openModal = () => {
    setShowModal(true);
    setSearchQuery('');
    fetchAllChannels(0, '');
  };

  const handleSearch = () => {
    fetchAllChannels(0, searchQuery);
  };

  const handleNextPage = () => {
    const newSkip = pagination.skip + pagination.limit;
    if (newSkip < pagination.total) {
      fetchAllChannels(newSkip, searchQuery);
    }
  };

  const handlePrevPage = () => {
    const newSkip = Math.max(0, pagination.skip - pagination.limit);
    fetchAllChannels(newSkip, searchQuery);
  };

  const getChannelInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : 'C';
  };

  const getGradientColor = (index) => {
    const colors = [
      'from-red-500 to-pink-500',
      'from-blue-500 to-cyan-500',
      'from-green-500 to-emerald-500',
      'from-purple-500 to-violet-500',
      'from-orange-500 to-amber-500',
      'from-teal-500 to-cyan-500',
      'from-indigo-500 to-blue-500',
      'from-rose-500 to-red-500',
      'from-yellow-500 to-orange-500',
      'from-fuchsia-500 to-pink-500',
    ];
    return colors[index % colors.length];
  };

  const ChannelCard = ({ channel, index, isModal = false }) => (
    <a
      href={channel.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`group bg-white rounded-2xl border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden ${isModal ? 'p-4' : 'p-6'}`}
      data-testid={`channel-card-${index}`}
    >
      <div className="flex items-center gap-4">
        <div className={`relative ${isModal ? 'w-14 h-14' : 'w-16 h-16'} flex-shrink-0`}>
          {channel.thumbnail ? (
            <img
              src={channel.thumbnail}
              alt={channel.name}
              className="w-full h-full rounded-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`${channel.thumbnail ? 'hidden' : 'flex'} w-full h-full rounded-full bg-gradient-to-br ${getGradientColor(index)} items-center justify-center text-white font-bold ${isModal ? 'text-lg' : 'text-xl'}`}
          >
            {getChannelInitial(channel.name)}
          </div>
          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
            <Youtube size={12} className="text-white" />
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className={`font-bold text-gray-900 group-hover:text-[#0077cc] transition-colors truncate ${isModal ? 'text-sm' : 'text-base'}`}>
            {channel.name}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full truncate">
              {channel.category || 'General'}
            </span>
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <Globe size={10} />
              {channel.language}
            </span>
          </div>
        </div>
        
        <ExternalLink size={16} className="text-gray-400 group-hover:text-[#0077cc] transition-colors flex-shrink-0" />
      </div>
    </a>
  );

  return (
    <section id="channels" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-medium mb-4">
            <Youtube size={16} />
            Global YouTube Network
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-[#0077cc]">YouTube Empire</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Channels reaching billions across the world in 50+ languages
          </p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 max-w-7xl mx-auto">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-200 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gray-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 max-w-7xl mx-auto">
            {topChannels.slice(0, 10).map((channel, index) => (
              <ChannelCard key={channel.id || index} channel={channel} index={index} />
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <button 
            onClick={openModal}
            className="group px-8 py-4 bg-gradient-to-r from-[#0077cc] to-[#00c6d7] text-white text-lg font-medium rounded-full hover:shadow-xl transition-all hover:scale-105 inline-flex items-center gap-2"
            data-testid="view-all-channels-btn"
          >
            <span>View All Channels</span>
            <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="channels-modal">
            <div className="bg-white rounded-3xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">All YouTube Channels</h3>
                    <p className="text-gray-600">{pagination.total} channels across 50+ languages</p>
                  </div>
                  <button 
                    onClick={() => setShowModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    data-testid="close-modal-btn"
                  >
                    <X size={24} />
                  </button>
                </div>
                
                {/* Search Only */}
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search channels by name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0077cc] focus:border-transparent outline-none"
                      data-testid="search-input"
                    />
                  </div>
                  
                  <button
                    onClick={handleSearch}
                    className="px-6 py-2.5 bg-[#0077cc] text-white rounded-xl hover:bg-[#0066b3] transition-colors font-medium"
                    data-testid="search-btn"
                  >
                    Search
                  </button>
                </div>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                {modalLoading ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="bg-gray-50 rounded-2xl p-4 animate-pulse">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-gray-200 rounded-full" />
                          <div className="flex-1">
                            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                            <div className="h-3 bg-gray-200 rounded w-1/2" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : allChannels.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {allChannels.map((channel, index) => (
                      <ChannelCard key={channel.id || index} channel={channel} index={pagination.skip + index} isModal />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Youtube size={48} className="mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No channels found</p>
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        fetchAllChannels(0, '');
                      }}
                      className="mt-4 text-[#0077cc] hover:underline"
                    >
                      Clear search
                    </button>
                  </div>
                )}
              </div>
              
              {pagination.total > pagination.limit && (
                <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    Showing {pagination.skip + 1}-{Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={pagination.skip === 0}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={pagination.skip + pagination.limit >= pagination.total}
                      className="px-4 py-2 bg-[#0077cc] text-white rounded-lg hover:bg-[#0066b3] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      data-testid="next-page-btn"
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default YouTubeChannels;
